import { useState } from 'react';
import { DeckExport } from './DeckExport';
import { Icon } from './Icon';
import { downloadArtifact } from '../api';
import { ArtifactViewer } from './ArtifactViewer';
import { STATUS_LABEL, type ArtifactName, type Job } from '../types';

interface Props {
  job: Job;
  token: string;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One row per document rather than one per file: the same protocol in two
 * formats is one thing to the reader, not two.
 */
interface DocumentEntry {
  label: string;
  hint: string;
  /** Opened by the eye, and downloaded by the button next to it. */
  text: ArtifactName;
  textLabel: string;
  textIcon: 'markdown' | 'json';
  /** Absent for JSON, and missing on jobs finished before PDFs existed. */
  pdf?: ArtifactName;
}

const DOCUMENTS: DocumentEntry[] = [
  { label: 'Todos', hint: 'Aufgaben nach Person gruppiert', text: 'todos', textLabel: 'MD', textIcon: 'markdown', pdf: 'todos-pdf' },
  {
    label: 'Zusammenfassung',
    hint: 'Kernpunkte, Entscheidungen, offene Fragen',
    text: 'summary',
    textLabel: 'MD',
    textIcon: 'markdown',
    pdf: 'summary-pdf',
  },
  {
    label: 'Transkript',
    hint: 'Vollständig, mit Sprechern und Zeitstempeln',
    text: 'transcript',
    textLabel: 'MD',
    textIcon: 'markdown',
    pdf: 'transcript-pdf',
  },
  { label: 'Gesamtprotokoll', hint: 'Alles in einer Datei', text: 'bundle', textLabel: 'MD', textIcon: 'markdown', pdf: 'bundle-pdf' },
  { label: 'Todos (JSON)', hint: 'Zur Weiterverarbeitung', text: 'todos-json', textLabel: 'JSON', textIcon: 'json' },
];

function formatDuration(seconds?: number): string | undefined {
  if (!seconds) return undefined;
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  return hh > 0 ? `${hh} h ${mm} min` : `${mm} min`;
}

export function JobCard({ job, token, expanded, onToggle }: Props) {
  const [downloadError, setDownloadError] = useState<string>();
  const [viewing, setViewing] = useState<ArtifactName>();

  const meta = [
    formatDuration(job.durationSeconds),
    job.speakerCount ? `${job.speakerCount} Sprecher` : undefined,
    job.todoCount !== undefined ? `${job.todoCount} Todos` : undefined,
    job.markerCount ? `${job.markerCount}× fürs Protokoll markiert` : undefined,
    new Date(job.createdAt).toLocaleString('de-DE'),
  ].filter(Boolean);

  const available = DOCUMENTS.filter((entry) => job.artifacts?.[entry.text]);

  async function download(artifact: ArtifactName) {
    setDownloadError(undefined);
    try {
      await downloadArtifact(token, job.jobId, artifact);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
    }
  }

  return (
    <li className={`job job-${job.status.toLowerCase()}`}>
      <button className="job-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="job-title">
          <strong>{job.title ?? job.fileName}</strong>
          {job.title && <span className="muted small"> · {job.fileName}</span>}
        </span>
        <span className={`badge badge-${job.status.toLowerCase()}`}>{STATUS_LABEL[job.status]}</span>
      </button>

      <p className="muted small job-meta">{meta.join(' · ')}</p>

      {job.status === 'FAILED' && job.error && <p className="error">{job.error}</p>}

      {expanded && (
        <div className="job-body">
          {available.length > 0 ? (
            <ul className="artifacts">
              {available.map((entry) => (
                <li key={entry.text}>
                  <span className="artifact-label">
                    <strong>{entry.label}</strong>
                    <span className="muted small">{entry.hint}</span>
                  </span>
                  <span className="artifact-actions">
                    <button
                      className="icon-button"
                      onClick={() => setViewing(entry.text)}
                      title={`${entry.label} ansehen`}
                      aria-label={`${entry.label} ansehen`}
                    >
                      <Icon name="eye" />
                    </button>
                    <button
                      className="icon-button"
                      onClick={() => void download(entry.text)}
                      title={`${entry.label} als ${entry.textLabel} herunterladen`}
                      aria-label={`${entry.label} als ${entry.textLabel} herunterladen`}
                    >
                      <Icon name={entry.textIcon} />
                    </button>
                    {/* Jobs finished before PDFs existed simply do not offer one. */}
                    {entry.pdf && job.artifacts?.[entry.pdf] && (
                      <button
                        className="icon-button"
                        onClick={() => void download(entry.pdf!)}
                        title={`${entry.label} als PDF herunterladen`}
                        aria-label={`${entry.label} als PDF herunterladen`}
                      >
                        <Icon name="pdf" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Noch keine Dateien verfügbar.</p>
          )}
          {downloadError && <p className="error">{downloadError}</p>}
          {/* Only where there is something to hand over: a meeting that produced
              no tasks has nothing to put on a board. */}
          {job.artifacts?.['todos-json'] && (job.todoCount ?? 0) > 0 && (
            <DeckExport jobId={job.jobId} token={token} />
          )}
        </div>
      )}

      {viewing && (
        <ArtifactViewer
          token={token}
          jobId={job.jobId}
          artifact={viewing}
          title={`${DOCUMENTS.find((e) => e.text === viewing)?.label} · ${job.title ?? job.fileName}`}
          onClose={() => setViewing(undefined)}
        />
      )}
    </li>
  );
}
